// src/pages/TeamSettingsPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { auth, db } from "../firebase";
import { ref as dbRef, onValue, update } from "firebase/database";
import { TeamLogo } from "../components/TeamLogo";

const PRESET_COLORS = [
  "#f5a623","#4ade80","#60a5fa","#f87171","#c084fc",
  "#fb923c","#34d399","#38bdf8","#f472b6","#a3e635",
  "#facc15","#e879f9","#2dd4bf","#f43f5e","#818cf8",
];

const PRESET_EMOJIS = [
  "🏏","⚡","🔥","🦁","🐯","🦅","⚔️","🛡️","👑","💎",
  "🚀","🌟","🎯","💪","🏆","🌊","🌪️","☄️","🐉","🦊",
];

export function TeamSettingsPage({ leagueCode }) {
  const uid = auth.currentUser?.uid;
  const fileInputRef = useRef(null);

  const [room,        setRoom]        = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [uploading,   setUploading]   = useState(false);
  const [status,      setStatus]      = useState("");
  const [previewUrl,  setPreviewUrl]  = useState(null); // local preview before upload

  const [teamName,  setTeamName]  = useState("");
  const [teamColor, setTeamColor] = useState("#f5a623");
  const [teamEmoji, setTeamEmoji] = useState("🏏");
  const [teamAbbr,  setTeamAbbr]  = useState("");
  const [teamImg,   setTeamImg]   = useState(""); // stored URL

  useEffect(() => {
    if (!leagueCode) return;
    const code = leagueCode.toUpperCase();
    const unsub = onValue(dbRef(db, `rooms/${code}`), s => setRoom(s.val() || null));
    return () => unsub();
  }, [leagueCode]);

  const mySlot = useMemo(() => {
    if (!uid || !room?.slots) return null;
    const entry = Object.entries(room.slots).find(([, v]) => v?.claimedBy === uid);
    return entry ? entry[0] : null;
  }, [uid, room?.slots]);

  const myCustom = useMemo(() => {
    if (!mySlot || !room) return null;
    return room.teamCustom?.[mySlot] || null;
  }, [mySlot, room]);

  useEffect(() => {
    if (!mySlot) return;
    setTeamName(myCustom?.name  || mySlot || "");
    setTeamColor(myCustom?.color || "#f5a623");
    setTeamEmoji(myCustom?.emoji || "🏏");
    setTeamAbbr(myCustom?.abbr   || (mySlot ? mySlot.replace("Team ", "T") : ""));
    setTeamImg(myCustom?.imgUrl  || "");
    setPreviewUrl(null);
    // Intentionally exclude myCustom: re-initializing on every Firebase update
    // would wipe a pending upload that hasn't been saved yet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mySlot]);

  // Resize image on canvas → base64 JPEG (~5–15KB), no Firebase Storage needed
  const processImage = (file) => new Promise((resolve, reject) => {
    const img = new Image();
    const blobUrl = URL.createObjectURL(file);
    img.onload = () => {
      const MAX = 160;
      const scale = Math.min(MAX / img.width, MAX / img.height, 1);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(blobUrl);
      resolve(canvas.toDataURL("image/jpeg", 0.75));
    };
    img.onerror = () => { URL.revokeObjectURL(blobUrl); reject(new Error("Could not load image")); };
    img.src = blobUrl;
  });

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setStatus("Image must be under 2MB."); return; }
    setUploading(true);
    setStatus("Processing…");
    try {
      const dataUrl = await processImage(file);
      setPreviewUrl(dataUrl);
      setTeamImg(dataUrl);
      setStatus("✓ Image ready — click Save to apply.");
    } catch (err) {
      setStatus("Failed to process image: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setTeamImg("");
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setStatus("");
  };

  const saveCustom = async () => {
    if (!mySlot || !leagueCode) return;
    setSaving(true);
    setStatus("Saving…");
    const code = leagueCode.toUpperCase();
    const name = teamName.trim() || mySlot;
    const abbr = teamAbbr.trim().toUpperCase().slice(0, 4) || name.slice(0, 3).toUpperCase();
    try {
      await update(dbRef(db), {
        [`rooms/${code}/teamCustom/${mySlot}/name`]:   name,
        [`rooms/${code}/teamCustom/${mySlot}/color`]:  teamColor,
        [`rooms/${code}/teamCustom/${mySlot}/emoji`]:  teamEmoji,
        [`rooms/${code}/teamCustom/${mySlot}/abbr`]:   abbr,
        [`rooms/${code}/teamCustom/${mySlot}/imgUrl`]: teamImg || null,
        [`rooms/${code}/slots/${mySlot}/name`]:        name,
      });
      setStatus("✓ Team saved!");
    } catch (e) {
      setStatus("Save failed: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const allSlots = Object.keys(room?.slots || {});
  const allTeamCustom = room?.teamCustom || {};
  const displayImg = previewUrl || teamImg;

  if (!mySlot) {
    return (
      <div className="panel" style={{ padding: 24, textAlign: "center" }}>
        <div style={{ color: "var(--text-dim)" }}>Join the draft room first to customize your team.</div>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 12, alignItems: "start" }}>
      {/* ── Editor ── */}
      <div className="panel">
        <div className="panel-head">
          <div style={{ fontWeight: 800, fontSize: 18, fontFamily: "'Playfair Display', Georgia, serif" }}>My Team</div>
          <div style={{ fontSize: 13, color: "var(--text-dim)" }}>{mySlot}</div>
        </div>
        <div style={{ padding: 20 }}>

          {/* Live preview */}
          <div style={{
            display: "flex", alignItems: "center", gap: 14, padding: 16,
            background: `linear-gradient(135deg, ${teamColor}22 0%, transparent 100%)`,
            borderRadius: "var(--radius)", border: `1px solid ${teamColor}44`, marginBottom: 24,
          }}>
            <TeamLogo imgUrl={displayImg} emoji={teamEmoji} name={teamName} color={teamColor} size={56} />
            <div>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 20, fontWeight: 900, color: teamColor }}>
                {teamName || mySlot}
              </div>
              <div style={{ marginTop: 3 }}>
                <span style={{ background: `${teamColor}22`, color: teamColor, padding: "1px 7px", borderRadius: 4, fontSize: 10, fontWeight: 800, letterSpacing: "0.06em" }}>
                  {teamAbbr || "TAG"}
                </span>
              </div>
            </div>
          </div>

          {/* Team logo upload */}
          <div style={{ marginBottom: 20 }}>
            <label className="label" style={{ display: "block", marginBottom: 10 }}>Team Logo</label>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <TeamLogo imgUrl={displayImg} emoji={teamEmoji} name={teamName} color={teamColor} size={64} />
              <div style={{ flex: 1 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  id="team-logo-upload"
                />
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <label htmlFor="team-logo-upload">
                    <div style={{
                      padding: "7px 14px", borderRadius: "var(--radius-sm)", cursor: "pointer",
                      background: "var(--bg2)", border: "1px solid var(--border2)",
                      fontSize: 13, fontWeight: 600, color: "var(--text)",
                      display: "inline-block",
                    }}>
                      {uploading ? "Uploading…" : displayImg ? "Change Photo" : "Upload Photo"}
                    </div>
                  </label>
                  {displayImg && (
                    <button className="mini" onClick={removeImage} type="button"
                      style={{ color: "var(--red)", borderColor: "rgba(248,113,113,0.3)" }}>
                      Remove
                    </button>
                  )}
                </div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
                  JPG, PNG, GIF up to 2MB. If no photo, emoji is used instead.
                </div>
              </div>
            </div>
          </div>

          {/* Team name */}
          <div style={{ marginBottom: 18 }}>
            <label className="label" style={{ display: "block", marginBottom: 8 }}>Team Name</label>
            <input className="search-box" value={teamName} onChange={e => { setTeamName(e.target.value); setStatus(""); }}
              placeholder={mySlot} maxLength={30} />
          </div>

          {/* Abbreviation */}
          <div style={{ marginBottom: 18 }}>
            <label className="label" style={{ display: "block", marginBottom: 8 }}>Abbreviation (3–4 letters)</label>
            <input className="search-box" value={teamAbbr}
              onChange={e => { setTeamAbbr(e.target.value.toUpperCase().slice(0, 4)); setStatus(""); }}
              placeholder="e.g. SKY" maxLength={4} style={{ width: 100 }} />
          </div>

          {/* Color */}
          <div style={{ marginBottom: 18 }}>
            <label className="label" style={{ display: "block", marginBottom: 10 }}>Team Color</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              {PRESET_COLORS.map(c => (
                <div key={c} onClick={() => { setTeamColor(c); setStatus(""); }} style={{
                  width: 28, height: 28, borderRadius: "50%", background: c, cursor: "pointer",
                  border: teamColor === c ? "3px solid #fff" : "2px solid transparent",
                  boxShadow: teamColor === c ? `0 0 0 2px ${c}` : "none",
                  transition: "all 0.1s",
                }} />
              ))}
              <input type="color" value={teamColor} onChange={e => { setTeamColor(e.target.value); setStatus(""); }}
                style={{ width: 32, height: 32, border: "none", padding: 0, background: "transparent", cursor: "pointer" }} />
            </div>
          </div>

          {/* Emoji (only shown if no image) */}
          {!displayImg && (
            <div style={{ marginBottom: 24 }}>
              <label className="label" style={{ display: "block", marginBottom: 10 }}>Team Icon (used if no photo)</label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {PRESET_EMOJIS.map(e => (
                  <div key={e} onClick={() => { setTeamEmoji(e); setStatus(""); }} style={{
                    width: 38, height: 38, borderRadius: "var(--radius-sm)", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20,
                    background: teamEmoji === e ? "rgba(245,166,35,0.15)" : "var(--bg2)",
                    border: teamEmoji === e ? "1px solid var(--gold)" : "1px solid var(--border)",
                  }}>
                    {e}
                  </div>
                ))}
              </div>
            </div>
          )}

          <button className="primary" onClick={saveCustom} disabled={saving || uploading} style={{ width: "100%" }}>
            {saving ? "Saving…" : "Save Team"}
          </button>
          {status && (
            <div style={{ marginTop: 10, fontSize: 13, color: status.startsWith("✓") ? "var(--green)" : "var(--red)" }}>
              {status}
            </div>
          )}
        </div>
      </div>

      {/* ── All teams sidebar ── */}
      <div className="panel" style={{ alignSelf: "start" }}>
        <div className="panel-head">
          <div style={{ fontWeight: 700, fontSize: 14 }}>All Teams</div>
          <span className="pill muted">{allSlots.length}</span>
        </div>
        {allSlots.length === 0 ? (
          <div style={{ padding: 16, color: "var(--text-muted)", fontSize: 13 }}>No teams yet.</div>
        ) : allSlots.map(slot => {
          const custom = allTeamCustom[slot] || {};
          const isMe = slot === mySlot;
          const name  = custom.name  || room?.slots?.[slot]?.name || slot;
          const color = custom.color || "var(--text-dim)";
          return (
            <div key={slot} style={{
              padding: "10px 14px", borderBottom: "1px solid var(--border)",
              display: "flex", alignItems: "center", gap: 10,
              background: isMe ? "rgba(245,166,35,0.04)" : "transparent",
            }}>
              <TeamLogo imgUrl={custom.imgUrl} emoji={custom.emoji} name={name} color={color} size={34} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: isMe ? "var(--gold)" : "var(--text)" }}>
                  {name}
                  {isMe && <span style={{ marginLeft: 6, fontSize: 10, color: "var(--gold)" }}>← you</span>}
                </div>
                <span style={{ background: `${color}22`, color, padding: "1px 5px", borderRadius: 3, fontWeight: 800, letterSpacing: "0.06em", fontSize: 9 }}>
                  {custom.abbr || slot.replace("Team ", "T")}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
